import { useEffectEvent } from '@floating-ui/react/utils';
import { Plus } from '@untitled-ui/icons-react';
import clsx from 'clsx';
import { type FC, useEffect, useState } from 'react';

import { ChatInput } from '@/components/chat/ChatInput';
import { ChatMessages } from '@/components/chat/ChatMessages';
import { ChatMode } from '@/components/chat/ChatMode';
import { ChatOptions } from '@/components/chat/ChatOptions';
import { useChatService } from '@/components/chat/chatService';
import { useAgentChat } from '@/components/chat/hooks/useAgentChat';
import { useChatMessaging } from '@/components/chat/hooks/useChatMessaging';
import { useChatOptions } from '@/components/chat/hooks/useChatOptions';
import { useChatSession } from '@/components/chat/hooks/useChatSession';
import { useChatStore } from '@/components/chat/useChatStore';
import { AISidePanelHeader } from '@/components/side-panel/ai/AISidePanelHeader';
import { SidePanelContainer } from '@/components/side-panel/SidePanelContainer';
import type { SidePanelState } from '@/components/side-panel/useSidePanelStore';
import { useSidePanelStore } from '@/components/side-panel/useSidePanelStore';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import styles from './style.module.scss';

let restartChat = false;

useSidePanelStore.subscribe(
  (state: SidePanelState, prevState: SidePanelState) => {
    if (state.key !== prevState.key) {
      restartChat = true;
    }
  }
);

export const AIChatSidePanel: FC = () => {
  const {
    clearMessages,
    clearOptions,
    setSessionId,
    setSessionInitialized,
    sessionId,
    isLoading,
    isInitialising,
    messages,
    options,
    chatMode,
    setChatMode,
  } = useChatStore();

  const [inputValue, setInputValue] = useState('');

  useChatSession();

  const isAgentEnabled = useIsModuleEnabled('ai.subModules.agent');

  // Handle message sending
  const { sendMessage } = useChatMessaging();
  const { sendAgentMessage } = useAgentChat();

  // Get chat service for session management
  const { clearSession } = useChatService();

  useChatOptions(sendMessage);

  function handleNewChat() {
    clearMessages();
    clearOptions();
    clearSession(); // Clear the active session from socket provider
    setSessionId(null);
    setSessionInitialized(false);
  }

  const handleModeChange = (mode: 'platform' | 'agent') => {
    if (mode === chatMode) {
      return;
    }
    // Switching mode starts a fresh conversation so the two modes' transcripts
    // never mix.
    handleNewChat();
    setChatMode(mode);
  };

  const handleSendMessage = async () => {
    const userMessage = inputValue.trim();
    if (!userMessage || isLoading || isInitialising) {
      return;
    }
    // Defence in depth: treat the turn as agent mode only when the flag is also
    // on, so a stale chatMode can never route a non-entitled user to the agent.
    const useAgent = chatMode === 'agent' && isAgentEnabled;
    // Platform chat needs an ai-engine session; agent mode is server-session
    // (the gateway owns it per user+org), so it has no client sessionId gate.
    if (!useAgent && !sessionId) {
      return;
    }

    setInputValue(''); // Clear input only after validation passes

    if (useAgent) {
      await sendAgentMessage(userMessage);
    } else {
      await sendMessage(userMessage);
    }
  };

  const startANewChat = useEffectEvent(() => {
    handleNewChat();
  });

  useEffect(() => {
    if (restartChat) {
      startANewChat();

      restartChat = false;
    }
  }, [startANewChat]);

  // Keep chatMode in sync with entitlement: if the agent submodule is disabled
  // (e.g. flipped off mid-session), fall back to platform so the store can never
  // hold 'agent' without the flag.
  useEffect(() => {
    if (!isAgentEnabled && chatMode === 'agent') {
      setChatMode('platform');
    }
  }, [isAgentEnabled, chatMode, setChatMode]);

  return (
    <SidePanelContainer
      header={
        <AISidePanelHeader
          title={
            chatMode === 'agent' ? 'RiskSmart Agent' : 'Platform Knowledge'
          }
          toolbarButtons={[
            ...(isAgentEnabled
              ? [
                  <ChatMode
                    key={'chat-mode'}
                    selectedMode={chatMode}
                    onModeChange={handleModeChange}
                  />,
                ]
              : []),
            <button onClick={handleNewChat} title={'New chat'} key={'new-chat'}>
              <Plus />
            </button>,
          ]}
        ></AISidePanelHeader>
      }
      content={
        <div className={clsx(styles.chatPanel, 'overflow-y-hidden')}>
          <ChatMessages
            messages={messages}
            isLoading={isLoading}
            isInitialising={isInitialising}
          />
          <ChatOptions options={options} />
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSendMessage}
            isLoading={isLoading || isInitialising}
          />
        </div>
      }
    ></SidePanelContainer>
  );
};
